import { toast } from '@postscript/components';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { OFFER_SCANNER_ENABLED } from 'components/admin/utils/feature-flags';
import { QueryCacheTimes } from 'components/flowBuilder/constants';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { api } from 'controllers/network/apiClient';
import {
  EditProposalRequest,
  LinkProposalRequest,
  OfferProposal,
  OfferProposalScanStatus,
  ProposalDisposition,
  ProposalListParams,
  ProposalListResponse,
  ProposalReviewScope,
  ProposalTargetPreview,
} from '../types';
import {
  ALL_OFFERS_QUERY_KEY,
  OFFER_PROPOSALS_QUERY_KEY,
  OFFER_PROPOSAL_SCAN_QUERY_KEY,
} from './useOffers';

export { OFFER_PROPOSALS_QUERY_KEY } from './useOffers';
export const PROPOSALS_PATH = '/v2/ai-components/offers/proposals';

export const REVIEWABLE_PROPOSALS_PARAMS: ProposalListParams = {
  reviewable: true,
  grouped: true,
  limit: 50,
};

export const REVIEW_PAGE_SIZE = 25;

// The client throws a message string by default, which drops the status the
// 403 and 409 checks below depend on.
const STRUCTURED_ERRORS = { throwErrorAsString: false } as const;

export const reviewableProposalsParams = (
  scope: ProposalReviewScope,
  lastCompletedRunId?: string | null,
): ProposalListParams => ({
  ...REVIEWABLE_PROPOSALS_PARAMS,
  ...(scope === ProposalReviewScope.THIS_SCAN &&
    lastCompletedRunId && { observedInRun: lastCompletedRunId }),
});

export async function getOfferProposals(
  params?: ProposalListParams,
): Promise<ProposalListResponse> {
  const searchParams = new URLSearchParams();

  if (params?.status) searchParams.set('status', params.status);
  if (params?.disposition) searchParams.set('disposition', params.disposition);
  if (params?.runId) searchParams.set('runId', params.runId);
  if (params?.observedInRun) {
    searchParams.set('observedInRun', params.observedInRun);
  }
  if (params?.reviewable !== undefined) {
    searchParams.set('reviewable', params.reviewable.toString());
  }
  if (params?.grouped !== undefined) {
    searchParams.set('grouped', params.grouped.toString());
  }
  if (params?.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }

  const queryString = searchParams.toString();
  const url = queryString ? `${PROPOSALS_PATH}?${queryString}` : PROPOSALS_PATH;

  return api.get(url, STRUCTURED_ERRORS);
}

export const useOfferProposals = (
  params?: ProposalListParams,
  options?: { enabled?: boolean; refetchInterval?: number },
): UseQueryResult<ProposalListResponse> => {
  const { hasLabsFlag } = usePSLabs();

  return useQuery({
    queryKey: [OFFER_PROPOSALS_QUERY_KEY, params],
    queryFn: () => getOfferProposals(params),
    staleTime: QueryCacheTimes.MEDIUM,
    keepPreviousData: true,
    // Most shops lack the Offer Scanner labs flag and the endpoint 403s for
    // them, so don't issue a request that is guaranteed to fail. The labs
    // payload omits legacy shop_feature_flags-only enablement, so the 403
    // handling below stays as the server-authoritative backstop.
    enabled: hasLabsFlag(OFFER_SCANNER_ENABLED) && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    // 403 means the Offer Scanner flag is off for this shop -- a stable
    // state, not a transient failure. The contract made it distinct from an
    // empty list on purpose; retrying would never change the answer.
    retry: false,
  });
};

export async function getOfferProposalScanStatus(): Promise<OfferProposalScanStatus> {
  return api.get(`${PROPOSALS_PATH}/scan`);
}

export const useOfferProposalScanStatus =
  (): UseQueryResult<OfferProposalScanStatus> => {
    const { hasLabsFlag } = usePSLabs();

    return useQuery({
      queryKey: [OFFER_PROPOSAL_SCAN_QUERY_KEY],
      queryFn: getOfferProposalScanStatus,
      enabled: hasLabsFlag(OFFER_SCANNER_ENABLED),
      refetchOnWindowFocus: false,
      refetchInterval: (data) => (data?.scanning ? 2000 : 5000),
      retry: false,
    });
  };

export async function getOfferProposal(id: number): Promise<OfferProposal> {
  return api.get(`${PROPOSALS_PATH}/${id}`);
}

export const useOfferProposal = (
  id: number | null,
): UseQueryResult<OfferProposal> => {
  return useQuery({
    queryKey: [OFFER_PROPOSALS_QUERY_KEY, 'detail', id],
    queryFn: () => getOfferProposal(id as number),
    enabled: id !== null,
  });
};

export async function getProposalTargetPreview(
  id: number,
): Promise<ProposalTargetPreview> {
  return api.get(`${PROPOSALS_PATH}/${id}/target-preview`);
}

export const useProposalTargetPreview = (
  id: number | null,
  proposalUpdatedAt?: string,
): UseQueryResult<ProposalTargetPreview> =>
  useQuery({
    queryKey: [
      OFFER_PROPOSALS_QUERY_KEY,
      'target-preview',
      id,
      proposalUpdatedAt,
    ],
    queryFn: () => getProposalTargetPreview(id as number),
    enabled: id !== null,
    staleTime: QueryCacheTimes.MEDIUM,
  });

const invalidateProposalViews = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: [OFFER_PROPOSALS_QUERY_KEY] });
  // Approving creates or enriches offers, so the offer lists are stale too.
  // An edit alone does not, but it is the same set of views either way and
  // the queue and the offer lists are read side by side.
  queryClient.invalidateQueries({ queryKey: [ALL_OFFERS_QUERY_KEY] });
};

const hasResponseStatus = (error: unknown, status: number): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  (error as { response?: { status?: number } }).response?.status === status;

const isConflict = (error: unknown): boolean => hasResponseStatus(error, 409);

/** The shop does not have the Offer Scanner flag; a stable state. */
export const isForbiddenError = (error: unknown): boolean =>
  hasResponseStatus(error, 403);

const staleReviewToast = () =>
  toast.error(
    'This offer changed. Review the refreshed details and try again.',
  );

export interface ApproveProposalRequest {
  id: number;
  /**
   * Approve as a new draft offer rather than touching the offer the scanner
   * matched: on `enrich` this skips the update, on `possible_match` it skips
   * the link. The server 409s on any other disposition.
   */
  asNew?: boolean;
  /**
   * Never sent: approving is addressed by id alone. It is carried so the
   * success toast can say which of the two things approving did.
   */
  disposition?: ProposalDisposition;
  expectedProposalUpdatedAt?: string;
  expectedTargetUpdatedAt?: string;
}

/** The id alone still approves, so existing call sites keep working. */
export type ApproveProposalVariables = number | ApproveProposalRequest;

const approveRequest = (
  variables: ApproveProposalVariables,
): ApproveProposalRequest =>
  typeof variables === 'number' ? { id: variables } : variables;

/**
 * A plain approval of an `enrich` finding folds it into an offer that
 * already exists. Every other approval -- including an `enrich` or possible
 * match approved `asNew` -- creates a draft offer.
 */
const updatesExistingOffer = ({
  asNew,
  disposition,
}: ApproveProposalRequest): boolean =>
  disposition === ProposalDisposition.ENRICH && !asNew;

export async function approveProposal({
  id,
  asNew,
  expectedProposalUpdatedAt,
  expectedTargetUpdatedAt,
}: ApproveProposalRequest): Promise<OfferProposal> {
  return api.post(
    `${PROPOSALS_PATH}/${id}/approve`,
    {
      ...(asNew && { asNew }),
      ...(expectedProposalUpdatedAt && { expectedProposalUpdatedAt }),
      ...(expectedTargetUpdatedAt && { expectedTargetUpdatedAt }),
    },
    STRUCTURED_ERRORS,
  );
}

export const useApproveProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: ApproveProposalVariables) =>
      approveProposal(approveRequest(variables)),
    onSuccess: (proposal, variables) => {
      queryClient.setQueryData(
        [OFFER_PROPOSALS_QUERY_KEY, 'detail', proposal.id],
        proposal,
      );
      invalidateProposalViews(queryClient);
      toast.success(
        updatesExistingOffer(approveRequest(variables))
          ? 'Offer updated.'
          : 'Draft offer created.',
      );
    },
    onError: (error: unknown, variables) => {
      console.error('Failed to approve proposal:', error);
      if (isConflict(error)) {
        invalidateProposalViews(queryClient);
        staleReviewToast();
        return;
      }
      toast.error(
        updatesExistingOffer(approveRequest(variables))
          ? 'Failed to update the offer. Please try again.'
          : 'Failed to create the draft offer. Please try again.',
      );
    },
  });
};

export async function rejectProposal(id: number): Promise<OfferProposal> {
  return api.post(`${PROPOSALS_PATH}/${id}/reject`, {}, STRUCTURED_ERRORS);
}

export const useRejectProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectProposal,
    onSuccess: (proposal) => {
      queryClient.setQueryData(
        [OFFER_PROPOSALS_QUERY_KEY, 'detail', proposal.id],
        proposal,
      );
      invalidateProposalViews(queryClient);
      toast.success('Offer dismissed.');
    },
    onError: (error: unknown) => {
      console.error('Failed to reject proposal:', error);
      toast.error('Failed to dismiss the offer. Please try again.');
    },
  });
};

export async function linkProposal({
  id,
  ...body
}: LinkProposalRequest & { id: number }): Promise<OfferProposal> {
  return api.post(`${PROPOSALS_PATH}/${id}/link`, body, STRUCTURED_ERRORS);
}

export const useLinkProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: linkProposal,
    onSuccess: (proposal) => {
      queryClient.setQueryData(
        [OFFER_PROPOSALS_QUERY_KEY, 'detail', proposal.id],
        proposal,
      );
      invalidateProposalViews(queryClient);
      toast.success('Match confirmed!');
    },
    onError: (error: unknown) => {
      console.error('Failed to confirm match:', error);
      if (isConflict(error)) {
        invalidateProposalViews(queryClient);
        staleReviewToast();
        return;
      }
      toast.error('Failed to confirm match. Please try again.');
    },
  });
};

export async function updateProposal({
  id,
  ...body
}: EditProposalRequest & { id: number }): Promise<OfferProposal> {
  return api.patch(`${PROPOSALS_PATH}/${id}`, body, STRUCTURED_ERRORS);
}

export const useUpdateProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProposal,
    onSuccess: (proposal) => {
      queryClient.setQueryData(
        [OFFER_PROPOSALS_QUERY_KEY, 'detail', proposal.id],
        proposal,
      );
      invalidateProposalViews(queryClient);
      toast.success('Offer details updated.');
    },
    onError: (error: unknown) => {
      console.error('Failed to update proposal:', error);
      if (isConflict(error)) {
        invalidateProposalViews(queryClient);
        staleReviewToast();
        return;
      }
      toast.error('Failed to save your changes. Please try again.');
    },
  });
};
